<zag-card class="entity-access-dialog" role="dialog" aria-modal="true" [attr.aria-label]="entityLabel()">
  <div card-header>
    <div class="entity-access-dialog__header-row">
      <div class="entity-access-dialog__identity">
        <zag-avatar [name]="entityLabel()" size="sm" [variant]="avatarVariant()"></zag-avatar>
        <div>
          <h3 class="zag-card-title">{{ entityLabel() }}</h3>
          <p class="zag-card-subtitle">{{ subtitle().key | translate: subtitle().params }}</p>
        </div>
      </div>
      @if (showReload()) {
        <button
          type="button"
          zagButton="tonal"
          zagType="secondary"
          zagSize="sm"
          class="entity-access-dialog__reload"
          [zagTooltip]="'entityAccess.dialog.reload' | translate"
          tooltipPosition="above"
          (click)="onReload()"
        >
          <span zagIcon="refresh"></span>
        </button>
      }
      <button
        type="button"
        zagButton="outlined"
        class="entity-access-dialog__close"
        [attr.aria-label]="'common.close' | translate"
        (click)="onCancel()"
      >
        <span zagIcon="close"></span>
      </button>
    </div>
  </div>

  <ng-template #permissionList let-permissions>
    <div class="entity-access-dialog__rows entity-access-dialog__rows--flat">
      @for (permission of permissions; track permission) {
        <div
          class="entity-access-dialog__row"
          [attr.data-permission]="permission"
          [class.entity-access-dialog__row--unlicensed]="isUnlicensed(permission)"
        >
          <p class="entity-access-dialog__row-desc">{{ 'entityAccess.permissions.' + permission | translate }}</p>
          @if (isUnlicensed(permission)) {
            <zag-badge color="neutral">{{ 'entityAccess.dialog.unlicensed' | translate }}</zag-badge>
          }
          <zag-switch
            [checked]="isSelected(permission)"
            [ariaLabel]="'entityAccess.permissions.' + permission | translate"
            [disabled]="isRowDisabled(permission)"
            (checkedChange)="onPermissionChange(permission, $event)"
          />
        </div>
      }
    </div>
  </ng-template>

  <ng-template #permissionRows>
    <div class="entity-access-dialog__sections">
      @for (section of permissionSections; track section.titleKey ?? 'all') {
        @if (section.titleKey) {
          <zag-accordion-item
            [title]="section.titleKey | translate"
            [badge]="selectedInSection(section)"
            [expanded]="isSectionExpanded(section)"
            (expandedChange)="onSectionExpandedChange(section, $event)"
          >
            <zag-switch
              slot="actions"
              [checked]="isGroupChecked(section)"
              [ariaLabel]="section.titleKey | translate"
              [disabled]="isGroupDisabled(section)"
              (checkedChange)="onGroupToggle(section, $event)"
            />
            @if (section.hintKey) {
              <p class="entity-access-dialog__hint">{{ section.hintKey | translate }}</p>
            }
            <ng-container [ngTemplateOutlet]="permissionList" [ngTemplateOutletContext]="{ $implicit: section.permissions }"></ng-container>
          </zag-accordion-item>
        } @else {
          <ng-container [ngTemplateOutlet]="permissionList" [ngTemplateOutletContext]="{ $implicit: section.permissions }"></ng-container>
        }
      }
    </div>
    @if (!canEdit() && !fullMode()) {
      <p class="entity-access-dialog__manage-hint">
        {{ 'entityAccess.dialog.manageRequired' | translate: { permission: managePermissionLabel } }}
      </p>
    }
    @if (data.entityType === 'calendar') {
      <div class="entity-access-dialog__notices">
        <zag-badge color="info" [accent]="true">{{ 'entityAccess.dialog.calendarCreateHint' | translate }}</zag-badge>
        <zag-badge color="neutral">{{ 'entityAccess.dialog.calendarLimitsHint' | translate }}</zag-badge>
      </div>
    }
    @if (saveError()) {
      <p class="entity-access-dialog__error" role="alert">{{ saveError() ?? '' | translate }}</p>
    }
  </ng-template>

  @if (isLoading()) {
    <zag-badge color="neutral">{{ 'entityAccess.dialog.loading' | translate }}</zag-badge>
  } @else if (loadError()) {
    <zag-badge color="error" [accent]="true">{{ loadError() ?? '' | translate }}</zag-badge>
  } @else if (revoked()) {
    <div class="entity-access-dialog__stack">
      <zag-badge color="error" [accent]="true">{{ 'entityAccess.dialog.revoked' | translate }}</zag-badge>
      <p class="entity-access-dialog__hint">{{ 'entityAccess.dialog.revokedHint' | translate }}</p>
    </div>
  } @else if (nonOwnerDenied()) {
    <zag-badge color="error" [accent]="true">{{ 'entityAccess.dialog.nonOwner' | translate }}</zag-badge>
  } @else if (!selectableMode && conflict()) {
    <zag-badge color="warning" [accent]="true">{{ 'entityAccess.dialog.conflict' | translate }}</zag-badge>
  } @else if (!selectableMode && fullMode()) {
    <div class="entity-access-dialog__panel">
      <zag-badge color="info">{{ 'entityAccess.dialog.fullMode' | translate }}</zag-badge>
      <p class="entity-access-dialog__hint">{{ 'entityAccess.dialog.fullModeHint' | translate }}</p>
    </div>
  } @else if (readOnly() && !selectableMode) {
    <p class="entity-access-dialog__section-title">{{ 'entityAccess.dialog.yourPermissions' | translate }}</p>
    <div class="entity-access-dialog__rows">
      @for (permission of viewerPermissions(); track permission) {
        <div class="entity-access-dialog__row" [attr.data-permission]="permission">
          <p class="entity-access-dialog__row-desc">{{ 'entityAccess.permissions.' + permission | translate }}</p>
        </div>
      } @empty {
        <zag-badge color="neutral">{{ 'entityAccess.dialog.noGrants' | translate }}</zag-badge>
      }
    </div>
    <p class="entity-access-dialog__manage-hint">
      {{ 'entityAccess.dialog.manageRequired' | translate: { permission: managePermissionLabel } }}
    </p>
  } @else if (selectableMode) {
    <div class="entity-access-dialog__body">
      <div class="entity-access-dialog__user-panel">
        <zag-search
          [placeholder]="'common.search' | translate"
          [searchTerm]="searchTerm()"
          (searchTermChange)="onSearchChange($event)"
          [scopes]="recipientScopes()"
          [scope]="recipientScope()"
          (scopeChange)="onScopeChange($event)"
          [scopeAriaLabel]="'entityAccess.dialog.scopeAria' | translate"
        />
        <div class="entity-access-dialog__user-rows">
          @for (grantee of visibleGrantees(); track grantee.kind + ':' + grantee.id) {
            <zag-selection-row
              [label]="grantee.label"
              [avatarName]="grantee.kind === 'user' ? grantee.label : ''"
              [avatarSrc]="grantee.kind === 'user' ? (grantee.avatarSrc ?? null) : null"
              [icon]="grantee.kind === 'group' ? 'group' : null"
              [description]="granteeDescription(grantee)"
              [checked]="isGranteeSelected(grantee)"
              [badge]="granteeBadgeKey(grantee) | translate"
              [badgeColor]="granteeBadgeColor(grantee)"
              (checkedChange)="onToggleGrantee(grantee, $event)"
            />
          } @empty {
            @if (granteesLoading()) {
              <zag-badge color="neutral">{{ 'entityAccess.dialog.loading' | translate }}</zag-badge>
            } @else {
              <zag-badge color="neutral">{{ 'common.noResults' | translate }}</zag-badge>
            }
          }
        </div>
      </div>

      <zag-drawer [open]="hasSelection()" mode="push" position="right" width="360px" [plain]="true" [showBackdrop]="false">
        <div class="entity-access-dialog__drawer-content">
          @if (isLoadingSelected()) {
            <zag-badge color="neutral">{{ 'entityAccess.dialog.loading' | translate }}</zag-badge>
          } @else if (conflict()) {
            <zag-badge color="warning" [accent]="true">{{ 'entityAccess.dialog.conflict' | translate }}</zag-badge>
          } @else if (fullMode()) {
            <zag-badge color="info">{{ 'entityAccess.dialog.fullMode' | translate }}</zag-badge>
            <p class="entity-access-dialog__hint">{{ 'entityAccess.dialog.fullModeHint' | translate }}</p>
          } @else {
            <p class="entity-access-dialog__section-title">{{ 'entityAccess.dialog.grantedPermissions' | translate }}</p>
            <ng-container [ngTemplateOutlet]="permissionRows"></ng-container>
          }
        </div>
      </zag-drawer>
    </div>
  } @else {
    <p class="entity-access-dialog__section-title">{{ 'entityAccess.dialog.grantedPermissions' | translate }}</p>
    <ng-container [ngTemplateOutlet]="permissionRows"></ng-container>
  }

  <div card-footer class="entity-access-dialog__footer">
    @if (isLoading()) {
      <button type="button" zagButton zagType="secondary" [disabled]="true">
        {{ 'entityAccess.dialog.cancel' | translate }}
      </button>
      <button type="button" zagButton [disabled]="true">{{ 'entityAccess.dialog.save' | translate }}</button>
    } @else if (loadError()) {
      <button type="button" zagButton zagType="secondary" (click)="onCancel()">
        {{ 'entityAccess.dialog.close' | translate }}
      </button>
      <button type="button" zagButton (click)="onReload()">{{ 'entityAccess.dialog.retry' | translate }}</button>
    } @else if (!selectableMode && (conflict() || revoked())) {
      <button type="button" zagButton zagType="secondary" (click)="onCancel()">
        {{ 'entityAccess.dialog.discard' | translate }}
      </button>
      <button type="button" zagButton [disabled]="true">{{ 'entityAccess.dialog.save' | translate }}</button>
    } @else if (!selectableMode && (fullMode() || nonOwnerDenied() || readOnly())) {
      <button type="button" zagButton zagType="secondary" (click)="onCancel()">
        {{ 'entityAccess.dialog.close' | translate }}
      </button>
      <button type="button" zagButton [disabled]="true">{{ 'entityAccess.dialog.save' | translate }}</button>
    } @else if (!hasSelection()) {
      <button type="button" zagButton zagType="secondary" (click)="onCancel()">
        {{ 'entityAccess.dialog.cancel' | translate }}
      </button>
      <button type="button" zagButton [disabled]="true">{{ 'entityAccess.dialog.save' | translate }}</button>
    } @else {
      @if (showRemoveAll()) {
        <button type="button" zagButton="outlined" zagType="error" [disabled]="!canSave()" (click)="onRemoveAll()">
          {{ 'entityAccess.dialog.removeAll' | translate }}
        </button>
      } @else {
        <button type="button" zagButton zagType="secondary" [disabled]="isSaving()" (click)="onEditorCancel()">
          {{ 'entityAccess.dialog.cancel' | translate }}
        </button>
      }
      <button type="button" zagButton [zagLoading]="isSaving()" [disabled]="!canSave()" (click)="onSave()">
        {{ 'entityAccess.dialog.save' | translate }}
      </button>
    }
  </div>
</zag-card>
